import {type ChangeEvent, useEffect, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Container,
    List,
    Pagination,
    Paper,
    Typography,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {useTranslation} from "react-i18next";

import {getMyCourseDetails} from "../api/api";
import {CourseDetailsResponse} from "../types";
import {useAlert} from "../context/AlertContext";
import BreadcrumbLink from "../components/BreadcrumbLink";

const MyCourseDetailsPage = () => {
    const {t} = useTranslation();

    const {courseId} = useParams<{ courseId?: string }>();
    const safeCourseId = courseId ?? "";
    const {showAlert} = useAlert();

    const [data, setData] = useState<CourseDetailsResponse | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const pageSize = 10;

    const fetchData = async (uiPage: number) => {
        if (!safeCourseId) return;

        setLoading(true);
        setHasError(false);

        try {
            const res = await getMyCourseDetails(Number(safeCourseId), uiPage - 1, pageSize);
            setData(res);
            setCurrentPage((res.chapters?.page?.number ?? 0) + 1);
        } catch (err: any) {
            setHasError(true);
            setData(null);
            showAlert(err?.message ?? t("errors.contactAdmins"), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeCourseId]);

    const handlePageChange = (_: ChangeEvent<unknown>, value: number) => {
        fetchData(value);
    };

    if (loading && !data) {
        return (
            <Container sx={{mt: 6, display: "flex", justifyContent: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (hasError) {
        return (
            <Container sx={{mt: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2}}>
                <Typography color="text.secondary">
                    {t("mycoursedetails.errors.loadFailed")}
                </Typography>

                <Box sx={{display: "flex", gap: 2}}>
                    <Button
                        variant="contained"
                        onClick={() => fetchData(currentPage)}
                    >
                        {t("common.retry")}
                    </Button>

                    <Button
                        component={Link}
                        to="/my-courses"
                        variant="outlined"
                    >
                        {t("common.backToMyCourses")}
                    </Button>
                </Box>
            </Container>
        );
    }

    if (!data) return null;

    const totalPages = data.chapters?.page?.totalPages ?? 1;
    const chapters = data.chapters?.content ?? [];

    return (
        <Box sx={{px: {xs: 3, md: 3}}}>
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                sx={{mb: 2}}
            >
                <BreadcrumbLink to="/my-courses">
                    {t("breadcrumb.myCourses")}
                </BreadcrumbLink>

                <Typography color="text.primary" variant="body2">
                    <strong>{data.title || t("breadcrumb.courseDetails")}</strong>
                </Typography>
            </Breadcrumbs>

            <Box sx={{mb: 3}}>
                <Typography sx={{fontSize: 28, fontWeight: 800}} noWrap>
                    {data.title}
                </Typography>

                <Typography variant="body2" sx={{color: "text.secondary", mt: 0.5}}>
                    {data.description}
                </Typography>
            </Box>

            <List sx={{display: "flex", flexDirection: "column", gap: 3}}>
                {chapters.map((chapter) => (
                    <Paper
                        key={chapter.id}
                        component={Link}
                        to={`/chapter-details/${chapter.id}`}
                        elevation={0}
                        sx={(theme) => ({
                            px: 3,
                            py: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            textDecoration: "none",
                            color: "inherit",
                            cursor: "pointer",
                            transition: "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
                            "&:hover": {
                                transform: "translateY(-3px)",
                                boxShadow:
                                    theme.palette.mode === "dark"
                                        ? "0 10px 24px rgba(0,0,0,0.35)"
                                        : "0 10px 24px rgba(0,0,0,0.08)",
                                backgroundColor: theme.palette.action.hover,
                            },
                            "&:hover .chapter-arrow": {
                                transform: "translateX(4px)",
                            },
                            "&:focus-visible": {
                                outline: `2px solid ${theme.palette.primary.main}`,
                                outlineOffset: 2,
                            },
                        })}
                    >
                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2}}>
                            <Box sx={{minWidth: 0}}>
                                <Typography sx={{fontWeight: 700}} noWrap>
                                    {chapter.title}
                                </Typography>
                            </Box>

                            <ArrowForwardIosIcon
                                className="chapter-arrow"
                                sx={{
                                    fontSize: 16,
                                    color: "text.secondary",
                                    transition: "transform 160ms ease",
                                    flexShrink: 0,
                                }}
                            />
                        </Box>
                    </Paper>
                ))}
            </List>

            {chapters.length === 0 && (
                <Typography sx={{mt: 3}} color="text.secondary">
                    {t("mycoursedetails.noChapters")}
                </Typography>
            )}

            <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                />
            </Box>
        </Box>
    );
};

export default MyCourseDetailsPage;